import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import { useQuery } from 'react-query';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { format, subMonths, startOfMonth, endOfMonth, eachMonthOfInterval } from 'date-fns';
import { getCategoryColor } from '../utils/categoryColors';
import { useUserPreferences } from '../hooks/useUserPreferences';
import { formatCurrency, getMonthlyCostInCurrency } from '../utils/currency';
import ServiceLogo from '../components/ServiceLogo';

// Custom tooltip component for charts
const CustomTooltip = ({ active, payload, label, formatter }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <Box
        sx={{
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 1.5,
          boxShadow: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          {data.serviceName && (
            <ServiceLogo
              serviceName={data.serviceName}
              category={data.category || 'other'}
              size="small"
            />
          )}
          <Typography variant="body2" fontWeight="medium">
            {label || data.name}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {formatter ? formatter(payload[0].value) : payload[0].value}
        </Typography>
      </Box>
    );
  }
  return null;
};

const Analytics: React.FC = () => {
  const { user } = useAuth();
  const { getMainCurrency } = useUserPreferences(user?.id);
  const theme = useTheme();
  const [selectedPeriod, setSelectedPeriod] = useState('6');

  const { data: subscriptions } = useQuery(
    ['subscriptions', user?.id],
    async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .order('next_renewal_date', { ascending: true });

      if (error) throw error;
      console.log('Analytics: Raw subscription data:', data);
      return data || [];
    },
    {
      enabled: !!user,
    }
  );



  const calculateMonthlyCost = (sub: any) => {
    const result = getMonthlyCostInCurrency(sub, getMainCurrency());
    console.log(`Converting ${sub.name}: ${sub.cost} ${sub.currency} to ${getMainCurrency()} = ${result}`);
    return result;
  };

  const getCategoryData = () => {
    if (!subscriptions) return [];
    
    const categoryTotals: { [key: string]: number } = {};
    
    subscriptions.forEach(sub => {
      const monthlyCost = calculateMonthlyCost(sub);
      categoryTotals[sub.category] = (categoryTotals[sub.category] || 0) + monthlyCost;
    });

    return Object.entries(categoryTotals).map(([category, value]) => ({
      name: category.charAt(0).toUpperCase() + category.slice(1),
      value: parseFloat(value.toFixed(2)),
      color: getCategoryColor(category),
    }));
  };

  const getMonthlyData = () => {
    if (!subscriptions) return [];
    
    const months = eachMonthOfInterval({
      start: subMonths(new Date(), parseInt(selectedPeriod)),
      end: new Date(),
    });

    return months.map(month => {
      const monthStart = startOfMonth(month);
      const monthEnd = endOfMonth(month);
      
      const monthTotal = subscriptions.reduce((sum, sub) => {
        const subDate = new Date(sub.next_renewal_date);
        if (subDate >= monthStart && subDate <= monthEnd) {
          return sum + calculateMonthlyCost(sub);
        }
        return sum;
      }, 0);

      return {
        month: format(month, 'MMM yyyy'),
        total: parseFloat(monthTotal.toFixed(2)),
      };
    });
  };

  const getTopSubscriptions = () => {
    if (!subscriptions) return [];
    
    return subscriptions
      .map(sub => ({
        name: sub.name,
        serviceName: sub.name,
        cost: calculateMonthlyCost(sub),
        category: sub.category,
        color: getCategoryColor(sub.category),
      }))
      .sort((a, b) => b.cost - a.cost)
      .slice(0, 5);
  };

  const categoryData = getCategoryData();
  const monthlyData = getMonthlyData();
  const topSubscriptions = getTopSubscriptions();

  const totalMonthlySpending = subscriptions?.reduce((sum, sub) => sum + calculateMonthlyCost(sub), 0) || 0;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Spending Analytics
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Analyze your subscription spending patterns and trends.
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Total Monthly Spending
              </Typography>
              <Typography variant="h4" component="div">
                {formatCurrency(totalMonthlySpending, getMainCurrency())}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Across {subscriptions?.length || 0} subscriptions
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Average per Subscription
              </Typography>
              <Typography variant="h4" component="div">
                {formatCurrency(subscriptions?.length ? (totalMonthlySpending / subscriptions.length) : 0, getMainCurrency())}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Monthly average
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Categories
              </Typography>
              <Typography variant="h4" component="div">
                {new Set(subscriptions?.map(s => s.category) || []).size}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Different subscription categories
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3}>
        {/* Category Breakdown Pie Chart */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Spending by Category
            </Typography>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [formatCurrency(value as number, getMainCurrency()), 'Monthly Cost']} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Monthly Spending Trend */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Monthly Spending Trend
              </Typography>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Period</InputLabel>
                <Select
                  value={selectedPeriod}
                  label="Period"
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                >
                  <MenuItem value="3">3 Months</MenuItem>
                  <MenuItem value="6">6 Months</MenuItem>
                  <MenuItem value="12">12 Months</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value) => [formatCurrency(value as number, getMainCurrency()), 'Total Spending']} />
                  <Line
                    type="monotone"
                    dataKey="total"
                    stroke={theme.palette.primary.main}
                    strokeWidth={2}
                    dot={{ fill: theme.palette.primary.main, strokeWidth: 2, r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Top Subscriptions Bar Chart */}
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Top Subscriptions by Cost
            </Typography>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topSubscriptions}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip content={<CustomTooltip formatter={(value: number) => formatCurrency(value, getMainCurrency())} />} />
                  <Bar dataKey="cost" fill={theme.palette.primary.main}>
                    {topSubscriptions.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Category Comparison */}
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Category Comparison
            </Typography>
            <Grid container spacing={2}>
              {categoryData.map((category) => (
                <Grid item xs={12} sm={6} md={4} key={category.name}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            backgroundColor: category.color,
                            borderRadius: '50%',
                          }}
                        />
                        <Typography variant="body1" fontWeight="bold">
                          {category.name}
                        </Typography>
                      </Box>
                      <Typography variant="h6">
                        {formatCurrency(category.value, getMainCurrency())}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {((category.value / totalMonthlySpending) * 100).toFixed(1)}% of total
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Analytics; 